import type { AuditEntry } from "@kontaktfix/shared";
import { formatPhone } from "@kontaktfix/shared";
import { Avatar, EmptyState, Skeleton } from "@kontaktfix/ui";
import { CaretDownIcon, ClockCounterClockwiseIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { LogoMark } from "@/components/logo";
import { formatDateTime, formatRelative } from "@/lib/i18n";
import { useActivity } from "@/lib/queries";

function formatValue(value: unknown, empty: string): string {
  if (value === null || value === undefined || value === "") return empty;
  if (Array.isArray(value)) return value.join(", ") || empty;
  if (typeof value === "object") {
    const parts = Object.values(value as Record<string, unknown>).filter((v) => typeof v === "string" && v);
    return parts.join(", ") || empty;
  }
  if (typeof value === "string" && /^\+\d{7,15}$/.test(value)) return formatPhone(value);
  return String(value);
}

export function useFieldLabel() {
  const { t, i18n } = useTranslation();
  return (field: string) => {
    for (const key of [
      `contacts.fields.${field}`,
      `contacts.addressTypes.${field}`,
      `lists.form.${field}`,
      `settings.delivery.${field}`,
    ]) {
      if (i18n.exists(key)) return t(key);
    }
    return field;
  };
}

export function AuditLine({ entry, showEntity = true }: { entry: AuditEntry; showEntity?: boolean }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const label = useFieldLabel();
  const meta = entry.meta ?? {};
  const actionKey = `activity.actions.${entry.action}`;
  const actionText = i18n.exists(actionKey)
    ? t(actionKey, {
        count: Number(meta.count ?? 0),
        target: String(meta.targetName ?? ""),
        created: Number(meta.created ?? 0),
        updated: Number(meta.updated ?? 0),
        removed: Number(meta.removed ?? 0),
      })
    : entry.action;
  const changes = entry.changes ? Object.entries(entry.changes) : [];
  const actor = entry.actorKind === "system" ? t("activity.system") : entry.actorName;

  return (
    <div className="flex gap-3 py-3">
      {entry.actorKind === "system" ? (
        <span className="shrink-0">
          <LogoMark size={26} />
        </span>
      ) : (
        <Avatar name={actor} size={26} />
      )}
      <div className="min-w-0 flex-1">
        <div className="text-[13px] leading-5">
          <span className="font-medium">{actor}</span> <span className="text-fg-muted">{actionText}</span>{" "}
          {showEntity && entry.entityLabel && <span className="font-medium">{entry.entityLabel}</span>}
        </div>
        <div className="mt-0.5 flex items-center gap-2 text-[12px] text-fg-subtle">
          <span title={formatDateTime(entry.createdAt)}>{formatRelative(entry.createdAt)}</span>
          {changes.length > 0 && (
            <button
              type="button"
              className="flex items-center gap-0.5 hover:text-fg"
              onClick={() => setOpen(!open)}
            >
              {t("activity.showChanges")}
              <CaretDownIcon className={`size-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
          )}
        </div>
        {open && changes.length > 0 && (
          <div className="mt-2 overflow-hidden rounded-md border border-border text-[12.5px]">
            {changes.map(([field, change]) => (
              <div
                key={field}
                className="grid grid-cols-[120px_1fr] gap-3 border-b border-border px-3 py-1.5 last:border-b-0"
              >
                <span className="text-fg-muted">{label(field)}</span>
                <span className="min-w-0 break-words">
                  <span className="text-fg-subtle line-through">
                    {formatValue(change.from, t("activity.empty_value"))}
                  </span>
                  <span className="mx-1.5 text-fg-subtle">→</span>
                  <span>{formatValue(change.to, t("activity.empty_value"))}</span>
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function EntityHistory({ entityType, entityId }: { entityType: string; entityId: string }) {
  const { t } = useTranslation();
  const { data, isLoading } = useActivity({ entityType, entityId, pageSize: 100 });
  if (isLoading) return <Skeleton className="h-24" />;
  if (!data?.items.length)
    return <EmptyState icon={<ClockCounterClockwiseIcon />} title={t("activity.empty")} />;
  return (
    <div className="divide-y divide-border">
      {data.items.map((entry) => (
        <AuditLine key={entry.id} entry={entry} showEntity={false} />
      ))}
    </div>
  );
}
